// Step B (product inputs) ↔ Encompass, shared by both Qualify paths (AURA and Without AURA):
// the field values Step B saves, the reverse read for hydration, and the eligibility-search
// request. Kept free of React/RTK so it can be unit-tested.
import {
	ENCOMPASS_FIELD_IDS,
	PREPAYMENT_PENALTY_FIELD_VALUES,
	LOAN_PURPOSE_ENCOMPASS_VALUES,
	OCCUPANCY_ENCOMPASS_VALUES,
	CITIZENSHIP_ENCOMPASS_VALUES,
	UNIT_COUNT_FROM_ENUM,
	HOUSING_EVENT_ENCOMPASS_VALUES,
	SEASONING_ENCOMPASS_VALUES,
	BANKRUPTCY_TYPE_ENCOMPASS_VALUES,
	BANKRUPTCY_OUTCOME_ENCOMPASS_VALUES,
	MORTGAGE_LATE_FIELD_IDS,
	LOAN_PURPOSE_FROM_ENCOMPASS,
	OCCUPANCY_FROM_ENCOMPASS,
	CITIZENSHIP_FROM_ENCOMPASS,
	HOUSING_EVENT_FROM_ENCOMPASS,
	SEASONING_FROM_ENCOMPASS,
	BANKRUPTCY_TYPE_FROM_ENCOMPASS,
	BANKRUPTCY_OUTCOME_FROM_ENCOMPASS,
	UNIT_ENUM_FROM_COUNT,
	PREPAYMENT_PENALTY_FROM_LABEL,
	toEncompassPropertyType,
	toPropertyTypeOption,
} from "./aura.constants";
import {
	INCOME_VERIFY_TYPE_FIELD_ID,
	fromEncompassIncomeVerifyType,
	toEncompassIncomeVerifyType,
} from "./income-verification-type";

// Builds the {id,value} array for updateFields from Panel B's productInputs state,
// mirroring create-loan-terms/index.jsx's updateLoanFields. Only pushes a field when
// Panel B actually has a value for it — never overwrites existing loan data with a blank.
export const buildProductInputsFieldValues = (s) => {
	const valuesArr = [];

	if (s.incomeVerificationType) {
		valuesArr.push({
			id: INCOME_VERIFY_TYPE_FIELD_ID,
			value: toEncompassIncomeVerifyType(s.incomeVerificationType),
		});
	}
	if (s.debtServiceCoverageRatio) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.DSCR, value: s.debtServiceCoverageRatio });
	}
	if (s.loanPurpose && LOAN_PURPOSE_ENCOMPASS_VALUES[s.loanPurpose]) {
		const lp = LOAN_PURPOSE_ENCOMPASS_VALUES[s.loanPurpose];
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.LOAN_PURPOSE, value: lp });
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.REFINANCE_CASH_OUT_TYPE,
			value: lp === "Purchase" ? "" : lp === "NoCash-Out Refinance" ? "NoCashOut" : "CashOut",
			lock: true,
		});
	}
	if (s.purchasePrice) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.PURCHASE_PRICE, value: String(s.purchasePrice).split(".")[0] });
	}
	if (s.estimatedValue) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.ESTIMATED_VALUE, value: String(s.estimatedValue).split(".")[0] });
	}
	if (s.purchasePrice || s.estimatedValue) {
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.PROPERTY_VALUE,
			value:
				s.loanPurpose === "Purchase"
					? String(s.purchasePrice || "").split(".")[0]
					: String(s.estimatedValue || "").split(".")[0],
		});
	}
	if (s.loanAmount) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.LOAN_AMOUNT, value: String(s.loanAmount).split(".")[0] });
	}
	if (s.occupancy && OCCUPANCY_ENCOMPASS_VALUES[s.occupancy]) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.OCCUPANCY, value: OCCUPANCY_ENCOMPASS_VALUES[s.occupancy] });
	}
	if (s.citizenship && CITIZENSHIP_ENCOMPASS_VALUES[s.citizenship]) {
		// Encompass has no Foreign National residency type: like the Dashboard and the wizard's
		// application step, it is NonPermanentResidentAlien plus CX.FOREIGNNATLBORR1 = Yes, and
		// the flag is reset to No for any other choice.
		const foreignNational = s.citizenship === "ForeignNational";
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.CITIZENSHIP,
			value: foreignNational ? "NonPermanentResidentAlien" : CITIZENSHIP_ENCOMPASS_VALUES[s.citizenship],
		});
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.FOREIGN_NATIONAL_BORROWER_1, value: foreignNational ? "Yes" : "No" });
	}
	if (s.financedProperties !== "") {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.FINANCED_PROPERTIES, value: s.financedProperties });
	}
	if (s.reserve !== "") {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.RESERVES, value: s.reserve });
	}
	if (s.interestOnly) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.INTEREST_ONLY, value: s.interestOnly === "Yes", lock: true });
	}
	if (s.escrowWaiver) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.ESCROW_WAIVER_INDICATOR, value: s.escrowWaiver === "Yes" });
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.ESCROW_WAIVER, value: s.escrowWaiver === "Yes" ? "Waived" : "Not Waived" });
	}
	if (s.shortTermRental) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.SHORT_TERM_RENTAL, value: s.shortTermRental === "Yes" });
	}
	if (s.zipcode) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.ZIP_CODE, value: s.zipcode });
	}
	if (s.numberOfUnits && UNIT_COUNT_FROM_ENUM[s.numberOfUnits]) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.NUMBER_OF_UNITS, value: UNIT_COUNT_FROM_ENUM[s.numberOfUnits] });
	}
	// 1041 takes Encompass values, not the dropdown's Optimal Blue codes (blank = single family).
	const encompassPropertyType = toEncompassPropertyType(s.propertyType, s.numberOfStories);
	if (encompassPropertyType !== undefined) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.PROPERTY_TYPE, value: encompassPropertyType });
	}
	if (s.housingEventType && HOUSING_EVENT_ENCOMPASS_VALUES[s.housingEventType] !== undefined) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.HOUSING_EVENT_TYPE, value: HOUSING_EVENT_ENCOMPASS_VALUES[s.housingEventType] });
	}
	if (s.housingEventSeasoning && SEASONING_ENCOMPASS_VALUES[s.housingEventSeasoning]) {
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.HOUSING_EVENT_SEASONING,
			value: SEASONING_ENCOMPASS_VALUES[s.housingEventSeasoning],
		});
	}
	if (s.bankruptcyType && BANKRUPTCY_TYPE_ENCOMPASS_VALUES[s.bankruptcyType] !== undefined) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.BANKRUPTCY_TYPE, value: BANKRUPTCY_TYPE_ENCOMPASS_VALUES[s.bankruptcyType] });
	}
	if (s.bankruptcyOutcome && BANKRUPTCY_OUTCOME_ENCOMPASS_VALUES[s.bankruptcyOutcome]) {
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.BANKRUPTCY_OUTCOME,
			value: BANKRUPTCY_OUTCOME_ENCOMPASS_VALUES[s.bankruptcyOutcome],
		});
	}
	if (s.bankruptcySeasoning && SEASONING_ENCOMPASS_VALUES[s.bankruptcySeasoning]) {
		valuesArr.push({
			id: ENCOMPASS_FIELD_IDS.BANKRUPTCY_SEASONING,
			value: SEASONING_ENCOMPASS_VALUES[s.bankruptcySeasoning],
		});
	}
	if (s.mortgageLatePayments) {
		valuesArr.push({ id: ENCOMPASS_FIELD_IDS.MORTGAGE_LATE_ANY, value: s.mortgageLatePayments === "Yes" ? "Y" : "N" });
	}
	// "No" late payments clears every bucket, so counts entered before switching to No
	// don't stay in Encompass (and disqualify the loan).
	Object.entries(MORTGAGE_LATE_FIELD_IDS).forEach(([key, id]) => {
		if (s.mortgageLatePayments === "No") valuesArr.push({ id, value: "0" });
		else if (s[key] !== "" && s[key] !== undefined) valuesArr.push({ id, value: s[key] });
	});

	return valuesArr;
};

const isTruthyField = (v) => v === true || v === "true" || v === "Y" || v === "Yes";

// Reverse of buildProductInputsFieldValues above — given the field() GET response for the
// same field IDs (see PRODUCT_INPUTS_FIELD_IDS), rebuilds a partial productInputs object
// for the fetch-on-open hydration. Only includes a key when Encompass actually returned a
// recognized, non-empty value for it, so callers can safely merge this over existing state
// without blanking out anything Encompass doesn't have an answer for. Every lookup here
// mirrors buildProductInputsFieldValues' own translation tables in reverse — this is the
// riskiest net-new piece of this change (no existing precedent to mirror), so each field
// needs verifying against a real saved value during QA rather than trusted blindly.
export const mapProductInputsFromFields = (fieldsResponse) => {
	// Encompass upper-cases fieldId in responses (CX.INCOMEVERIFYTYPE, CX.PREPAYMENTPENALTY…),
	// so key by upper case — exact-case lookups silently missed every CX.* field.
	const byId = {};
	(fieldsResponse || []).forEach((f) => {
		byId[String(f?.fieldId ?? "").toUpperCase()] = f.value;
	});
	const get = (id) => {
		const v = byId[String(id).toUpperCase()];
		return v === undefined || v === null || v === "" ? undefined : v;
	};

	const out = {};

	const incomeVerify = get(INCOME_VERIFY_TYPE_FIELD_ID);
	if (incomeVerify) out.incomeVerificationType = fromEncompassIncomeVerifyType(incomeVerify);

	const dscr = get(ENCOMPASS_FIELD_IDS.DSCR);
	if (dscr) out.debtServiceCoverageRatio = dscr;

	const loanPurpose = get(ENCOMPASS_FIELD_IDS.LOAN_PURPOSE);
	if (loanPurpose) out.loanPurpose = LOAN_PURPOSE_FROM_ENCOMPASS[loanPurpose];

	const purchasePrice = get(ENCOMPASS_FIELD_IDS.PURCHASE_PRICE);
	if (purchasePrice) out.purchasePrice = purchasePrice;

	const estimatedValue = get(ENCOMPASS_FIELD_IDS.ESTIMATED_VALUE);
	if (estimatedValue) out.estimatedValue = estimatedValue;

	const loanAmount = get(ENCOMPASS_FIELD_IDS.LOAN_AMOUNT);
	if (loanAmount) out.loanAmount = loanAmount;

	const occupancy = get(ENCOMPASS_FIELD_IDS.OCCUPANCY);
	if (occupancy) out.occupancy = OCCUPANCY_FROM_ENCOMPASS[occupancy];

	const citizenship = get(ENCOMPASS_FIELD_IDS.CITIZENSHIP);
	const foreignNationalFlag = get(ENCOMPASS_FIELD_IDS.FOREIGN_NATIONAL_BORROWER_1);
	// The wizard's terms step wrote "Y"; the Dashboard and application step write "Yes".
	if (foreignNationalFlag === "Y" || foreignNationalFlag === "Yes") out.citizenship = "ForeignNational";
	else if (citizenship) out.citizenship = CITIZENSHIP_FROM_ENCOMPASS[citizenship];

	const financedProperties = get(ENCOMPASS_FIELD_IDS.FINANCED_PROPERTIES);
	if (financedProperties !== undefined) out.financedProperties = financedProperties;

	const reserve = get(ENCOMPASS_FIELD_IDS.RESERVES);
	if (reserve !== undefined) out.reserve = reserve;

	const interestOnly = get(ENCOMPASS_FIELD_IDS.INTEREST_ONLY);
	if (interestOnly !== undefined) out.interestOnly = isTruthyField(interestOnly) ? "Yes" : "No";

	const escrowWaiver = get(ENCOMPASS_FIELD_IDS.ESCROW_WAIVER);
	if (escrowWaiver) out.escrowWaiver = escrowWaiver === "Waived" ? "Yes" : "No";

	const shortTermRental = get(ENCOMPASS_FIELD_IDS.SHORT_TERM_RENTAL);
	if (shortTermRental !== undefined) out.shortTermRental = isTruthyField(shortTermRental) ? "Yes" : "No";

	const zipcode = get(ENCOMPASS_FIELD_IDS.ZIP_CODE);
	if (zipcode) out.zipcode = zipcode;

	const numberOfUnits = get(ENCOMPASS_FIELD_IDS.NUMBER_OF_UNITS);
	if (numberOfUnits) out.numberOfUnits = UNIT_ENUM_FROM_COUNT[numberOfUnits];

	// 1041 holds Encompass values (Condominium, PUD…); the wizard leaves it blank for a
	// single-family home with one unit.
	const rawPropertyType = get(ENCOMPASS_FIELD_IDS.PROPERTY_TYPE);
	const propertyType =
		toPropertyTypeOption(rawPropertyType) ||
		(rawPropertyType === undefined && String(numberOfUnits) === "1" ? "SingleFamily" : "");
	if (propertyType) out.propertyType = propertyType;

	const housingEventType = get(ENCOMPASS_FIELD_IDS.HOUSING_EVENT_TYPE);
	if (housingEventType) out.housingEventType = HOUSING_EVENT_FROM_ENCOMPASS[housingEventType];

	const housingEventSeasoning = get(ENCOMPASS_FIELD_IDS.HOUSING_EVENT_SEASONING);
	if (housingEventSeasoning) out.housingEventSeasoning = SEASONING_FROM_ENCOMPASS[housingEventSeasoning];

	const bankruptcyType = get(ENCOMPASS_FIELD_IDS.BANKRUPTCY_TYPE);
	if (bankruptcyType) out.bankruptcyType = BANKRUPTCY_TYPE_FROM_ENCOMPASS[bankruptcyType];

	const bankruptcyOutcome = get(ENCOMPASS_FIELD_IDS.BANKRUPTCY_OUTCOME);
	if (bankruptcyOutcome) out.bankruptcyOutcome = BANKRUPTCY_OUTCOME_FROM_ENCOMPASS[bankruptcyOutcome];

	const bankruptcySeasoning = get(ENCOMPASS_FIELD_IDS.BANKRUPTCY_SEASONING);
	if (bankruptcySeasoning) out.bankruptcySeasoning = SEASONING_FROM_ENCOMPASS[bankruptcySeasoning];

	const mortgageLate = get(ENCOMPASS_FIELD_IDS.MORTGAGE_LATE_ANY);
	if (mortgageLate !== undefined) out.mortgageLatePayments = isTruthyField(mortgageLate) ? "Yes" : "No";

	Object.entries(MORTGAGE_LATE_FIELD_IDS).forEach(([key, id]) => {
		const v = get(id);
		if (v !== undefined) out[key] = v;
	});

	const prepay = get(ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY);
	if (prepay) out.prePaymentPenalty = PREPAYMENT_PENALTY_FROM_LABEL[prepay];

	const numberOfStories = get(ENCOMPASS_FIELD_IDS.NUMBER_OF_STORIES);
	if (numberOfStories) out.numberOfStories = numberOfStories;

	// Drop keys that resolved to undefined (an Encompass value with no recognized reverse
	// mapping) rather than writing `undefined` into Redux state.
	Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
	return out;
};

// Everything Step B saves before a search: prepayment penalty + stories (Optimal Blue reads
// them off the loan) and the product inputs above.
export const buildProductInputsSaveValues = (s) => {
	const prepay =
		PREPAYMENT_PENALTY_FIELD_VALUES[s.prePaymentPenalty] || PREPAYMENT_PENALTY_FIELD_VALUES.None;
	return [
		{ id: ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY, value: prepay.label },
		{ id: ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY_LABEL_2964, value: prepay.months > 0 ? prepay.label : 0 },
		{ id: ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY_LABEL_2217, value: prepay.months > 0 ? prepay.label : 0 },
		{ id: ENCOMPASS_FIELD_IDS.PREPAYMENT_PENALTY_MONTHS, value: prepay.months },
		{ id: ENCOMPASS_FIELD_IDS.NUMBER_OF_STORIES, value: s.numberOfStories || "1" },
		...buildProductInputsFieldValues(s),
	];
};

// The getEligibleProducts request. Optimal Blue reads most inputs off the saved loan; the
// backend patches its loan snapshot with these overrides (OptimalBlueIntegrations.update_loan).
// Values are Optimal Blue's own enums, as the Quick Pricer sends them.
export const buildEligibleSearchFormData = ({ s, loanGuid, loanNumber, user, selectedOrganization }) => {
	const formData = new FormData();
	formData.append("encompass_loan_guid", loanGuid);
	formData.append("encompass_loan_number", loanNumber || "");
	formData.append("propertyType", s.propertyType || "");
	formData.append("occupancy", s.occupancy || "");
	formData.append("shortTermRental", s.shortTermRental || "");
	formData.append("numberOfUnits", s.numberOfUnits || "");
	formData.append("zipcode", s.zipcode || "");
	formData.append("incomeVerificationType", s.incomeVerificationType || "");
	formData.append("debtServiceCoverageRatio", s.debtServiceCoverageRatio || "");
	formData.append("debtToIncomeRatio", s.debtToIncomeRatio || "");
	formData.append("citizenship", s.citizenship || "");
	formData.append("loan_channel", (user?.loan_channel || "").toUpperCase());
	formData.append("broker_email", user?.email || "");
	formData.append("compensation_paid", s.lenderPaidvsBorrowerPaid || "");
	if (selectedOrganization) formData.append("selectedOrganization", selectedOrganization);
	return formData;
};
